import React from "react";
import { OBJECT_TYPES, getObjectType } from '../../../utils/reassignmentTypes';
import {
  getRelativeTime,
  getExactTime,
  getFullDate,
} from "../../../utils/dateFormat";
import styles from "./AssignationHistoriqueTab.module.css";

/* ---------- Icons ---------- */

const HistoryIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v5h5" />
    <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
    <path d="M12 7v5l4 2" />
  </svg>
);

const ClockIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const UserIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const CheckIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PendingIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/* ---------- Helpers ---------- */

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
};

/* Deterministic avatar / accent color from a key */
const AVATAR_COLORS = [
  "#2A66C6", "#3C3489", "#0E8A6A", "#C2410C",
  "#9333EA", "#0891B2", "#BE185D", "#4D7C0F",
];
const colorFor = (key = "") => {
  let h = 0;
  for (let i = 0; i < String(key).length; i++)
    h = String(key).charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

/* Conformity → presentation */
const getConformity = (value) => {
  if (value === true) return { label: "Conforme", cls: "ok", icon: <CheckIcon /> };
  if (value === false) return { label: "Non conforme", cls: "ko", icon: <CrossIcon /> };
  return null;
};

/* ---------- Component ---------- */

export default function AssignationHistoriqueTab({ histoAffectations = [] }) {
  const hasItems = histoAffectations && histoAffectations.length > 0;

  /* Most recent first */
  const items = hasItems
    ? [...histoAffectations].sort(
      (a, b) => new Date(b.dateAffectation) - new Date(a.dateAffectation)
    )
    : [];

  return (
    <div className={styles.container}>
      {/* Section header */}
      <div className={styles.header}>
        <span className={styles.headerIcon}>
          <HistoryIcon />
        </span>
        <h3 className={styles.headerTitle}>Historique des affectations</h3>
        {hasItems && <span className={styles.headerCount}>{items.length}</span>}
      </div>

      {!hasItems ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <HistoryIcon />
          </div>
          <p className={styles.emptyText}>Aucune affectation enregistrée</p>
        </div>
      ) : (
        <div className={styles.timeline}>
          {items.map((item, index) => {
            const objType = getObjectType(item?.type);
            const TypeIcon = objType.Icon;
            const accent = colorFor(objType.label);
            const name = item?.idUser?.trim();
            const hasUser = !!name;
            const conf = getConformity(item?.assignationConforme);
            const relative = getRelativeTime(item?.dateAffectation);
            const exact = getExactTime(item?.dateAffectation);
            const fullDate = getFullDate(item?.dateAffectation);
            const isLatest = index === 0;

            return (
              <div key={item?.id ?? index} className={styles.row}>
                {/* Timeline rail */}
                <div className={styles.rail}>
                  <span
                    className={`${styles.node} ${isLatest ? styles.nodeLatest : ""}`}
                    style={{ backgroundColor: accent, "--accent": accent }}
                  >
                    <TypeIcon sx={{ fontSize: 17 }} />
                  </span>
                  {index < items.length - 1 && (
                    <span className={styles.connector} />
                  )}
                </div>

                {/* Card */}
                <div
                  className={`${styles.card} ${isLatest ? styles.cardLatest : ""}`}
                  style={isLatest ? { "--accent": accent } : undefined}
                >
                  <div className={styles.cardTop}>
                    <div className={styles.cardTitle}>
                      <span
                        className={styles.objectChip}
                        style={{ color: accent, backgroundColor: `${accent}14` }}
                      >
                        <TypeIcon sx={{ fontSize: 14 }} />
                        {objType.label}
                      </span>
                      {isLatest && (
                        <span className={styles.currentBadge}>Actuelle</span>
                      )}
                    </div>
                    {conf && (
                      <span
                        className={`${styles.confBadge} ${styles[`conf_${conf.cls}`]}`}
                      >
                        {conf.icon}
                        {conf.label}
                      </span>
                    )}

                  </div>

                  <div className={styles.assignee}>
                    {hasUser ? (
                      <span
                        className={styles.assigneeAvatar}
                        style={{ backgroundColor: colorFor(name) }}
                      >
                        {getInitials(name)}
                      </span>
                    ) : (
                      <span className={styles.assigneeAvatarEmpty}>
                        <UserIcon size={13} />
                      </span>
                    )}
                    <span
                      className={hasUser ? styles.assigneeName : styles.assigneeEmpty}
                    >
                      {hasUser ? name : "Non attribué"}
                    </span>
                  </div>

                  <div className={styles.timestamp} title={fullDate}>
                    <span
                      className={`${styles.relativeTime} ${relative.fresh ? styles.relativeTimeFresh : ""
                        }`}
                    >
                      <ClockIcon size={10} />
                      {relative.label}
                    </span>
                    <span className={styles.timeSeparator} />
                    <span className={styles.exactTime}>{exact}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}